import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Audit, AuditFinding, ComplianceCase, FollowUp, FollowUpQuery, FollowUpStatus, Obligation } from '@regigov/contracts';
import { auditsApi, followUpsApi, obligationsApi } from '../api/compliance';
import { participantsApi } from '../api/participants';
import { Field, ProblemAlert, StatusBadge, formatDateTime } from './ui';

/** Today in Sydney time, the calendar the API checks audit and follow-up dates against. */
const todayLocal = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());

function useRefresh() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['case'] });
    void qc.invalidateQueries({ queryKey: ['cases'] });
    void qc.invalidateQueries({ queryKey: ['audits'] });
    void qc.invalidateQueries({ queryKey: ['follow-ups'] });
  };
}

function FollowUpState({ f }: { f: AuditFinding }) {
  return (
    <>
      <StatusBadge status={f.status} /> {f.overdue && <StatusBadge status="overdue" />}
      {f.status === 'completed' && (
        <div className="muted">
          {f.completedAt ? formatDateTime(f.completedAt) : ''}
          {f.completedByName ? ` by ${f.completedByName}` : ''}: {f.completionNote}
          {f.completionEvidenceRef && (
            <>
              {' '}
              (<code>{f.completionEvidenceRef}</code>)
            </>
          )}
        </div>
      )}
    </>
  );
}

function Findings({ findings }: { findings: AuditFinding[] }) {
  if (findings.length === 0) return <span className="muted">—</span>;
  return (
    <ol style={{ margin: 0, paddingLeft: '1.2em' }}>
      {findings.map((f) => (
        <li key={f.id}>
          {f.finding} <span className="muted">· </span>
          <code>{f.obligationRef}</code>
          {f.obligation && <span className="muted"> {f.obligation.title}</span>}
          <div>
            Follow-up: {f.followUpAction} (due {f.dueOn}) <FollowUpState f={f} />
          </div>
        </li>
      ))}
    </ol>
  );
}

function Result({ a }: { a: Audit }) {
  if (a.status === 'cancelled') return <span className="muted">Cancelled{a.cancelledAt ? ` ${formatDateTime(a.cancelledAt)}` : ''}: {a.cancelReason}</span>;
  if (!a.conductedOn) return <span className="muted">Not yet conducted</span>;
  return (
    <>
      Conducted {a.conductedOn}
      <div style={{ whiteSpace: 'pre-wrap' }}>{a.conductSummary}</div>
      {a.evidenceRef && (
        <div>
          Evidence: <code>{a.evidenceRef}</code>
        </div>
      )}
      {a.closeSummary && <div className="muted">Closed{a.closedAt ? ` ${formatDateTime(a.closedAt)}` : ''}: {a.closeSummary}</div>}
    </>
  );
}

/** Audits on a case (C5): the list for everyone, plus CER planning, conducting, closing and cancelling while the case is open. */
export function AuditPanel({ c, mode }: { c: ComplianceCase; mode: 'cer' | 'participant' }) {
  const editable = mode === 'cer' && c.status !== 'closed';
  return (
    <>
      <h2>Audits</h2>
      {c.audits.length === 0 ? (
        <p className="muted">No audits.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Audit</th>
              <th>Scope</th>
              <th>Period</th>
              <th>Planned for</th>
              <th>Status</th>
              <th>Findings and follow-ups</th>
              <th>Result</th>
              {mode === 'cer' && <th>By</th>}
            </tr>
          </thead>
          <tbody>
            {c.audits.map((a) => (
              <tr key={a.id}>
                <td>
                  <code>{a.id}</code>
                  {mode === 'cer' && a.findings.length > 0 && (
                    <div>
                      <Link to={`/cer/compliance/follow-ups?auditId=${encodeURIComponent(a.id)}`}>Follow-ups →</Link>
                    </div>
                  )}
                </td>
                <td style={{ whiteSpace: 'pre-wrap' }}>
                  {a.scope}
                  <div className="muted">{a.reason}</div>
                </td>
                <td>
                  {a.periodFrom} to {a.periodTo}
                </td>
                <td>{a.plannedFor}</td>
                <td>
                  <StatusBadge status={a.status} />
                </td>
                <td>
                  <Findings findings={a.findings} />
                </td>
                <td>
                  <Result a={a} />
                </td>
                {mode === 'cer' && (
                  <td>
                    {a.plannedByName}
                    {a.conductedByName && <div className="muted">Conducted by {a.conductedByName}</div>}
                    {a.closedByName && <div className="muted">Closed by {a.closedByName}</div>}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {mode === 'cer' &&
        c.audits
          .filter((a) => a.status === 'conducted')
          .map((a) => <CloseAudit key={a.id} a={a} />)}
      {editable && (
        <>
          {c.audits
            .filter((a) => a.status === 'planned')
            .map((a) => (
              <ConductAudit key={a.id} a={a} />
            ))}
          <PlanAudit c={c} />
        </>
      )}
    </>
  );
}

function PlanAudit({ c }: { c: ComplianceCase }) {
  const refresh = useRefresh();
  const [scope, setScope] = useState('');
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');
  const [plannedFor, setPlannedFor] = useState(todayLocal());
  const [reason, setReason] = useState('');
  const plan = useMutation({
    mutationFn: () => auditsApi.plan({ caseId: c.id, scope, periodFrom, periodTo, plannedFor, reason }),
    onSuccess: () => (setScope(''), setReason(''), refresh()),
  });
  const busy = plan.isPending;
  return (
    <form className="form card" onSubmit={(e) => (e.preventDefault(), plan.mutate())}>
      <h2>Plan an audit</h2>
      <Field label="Scope" hint={`What of ${c.subjectParticipantName}'s records and practices the audit covers.`}>
        <textarea rows={2} value={scope} onChange={(e) => setScope(e.target.value)} required disabled={busy} />
      </Field>
      <div className="row">
        <Field label="Period from">
          <input type="date" value={periodFrom} onChange={(e) => setPeriodFrom(e.target.value)} required disabled={busy} />
        </Field>
        <Field label="Period to">
          <input type="date" value={periodTo} min={periodFrom || undefined} onChange={(e) => setPeriodTo(e.target.value)} required disabled={busy} />
        </Field>
        <Field label="Planned for">
          <input type="date" value={plannedFor} min={todayLocal()} onChange={(e) => setPlannedFor(e.target.value)} required disabled={busy} />
        </Field>
      </div>
      <Field label="Reason">
        <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} required disabled={busy} />
      </Field>
      <p className="muted">Linked to this case and recorded on its timeline, visible to {c.subjectParticipantName}.</p>
      <ProblemAlert error={plan.error} />
      <div>
        <button type="submit" disabled={busy || scope.trim().length < 3 || reason.trim().length < 3 || !periodFrom || !periodTo || !plannedFor}>
          Plan audit
        </button>
      </div>
    </form>
  );
}

type Draft = { finding: string; obligationId: string; followUpAction: string; dueOn: string };
const blank = (): Draft => ({ finding: '', obligationId: '', followUpAction: '', dueOn: '' });

/** Obligation versions in force on at least one day of the audit period; the server checks the same. */
const inForceDuring = (o: Obligation, a: Audit) => o.validFrom <= a.periodTo && (o.validTo === null || o.validTo > a.periodFrom);

function ConductAudit({ a }: { a: Audit }) {
  const refresh = useRefresh();
  const [conductedOn, setConductedOn] = useState(todayLocal());
  const [summary, setSummary] = useState('');
  const [evidenceRef, setEvidenceRef] = useState('');
  const [rows, setRows] = useState<Draft[]>([blank()]);
  const [cancelReason, setCancelReason] = useState('');
  const set = (n: number, patch: Partial<Draft>) => setRows(rows.map((r, k) => (k === n ? { ...r, ...patch } : r)));
  const obligations = useQuery({ queryKey: ['obligations'], queryFn: obligationsApi.list });
  const applicable = (obligations.data?.items ?? []).filter((o) => inForceDuring(o, a));
  const filled = rows.filter((r) => r.finding.trim() || r.obligationId || r.followUpAction.trim() || r.dueOn);
  const complete = filled.every((r) => r.finding.trim().length >= 3 && r.obligationId && r.followUpAction.trim().length >= 3 && r.dueOn);
  const findings = filled.map((r) => ({ finding: r.finding.trim(), obligationId: Number(r.obligationId), followUpAction: r.followUpAction.trim(), dueOn: r.dueOn }));
  const conduct = useMutation({ mutationFn: () => auditsApi.conduct(a.id, { conductedOn, summary, evidenceRef: evidenceRef.trim() || undefined, findings }), onSuccess: refresh });
  const cancel = useMutation({ mutationFn: () => auditsApi.cancel(a.id, { reason: cancelReason }), onSuccess: refresh });
  const busy = conduct.isPending || cancel.isPending;
  return (
    <>
      <form className="form card" onSubmit={(e) => (e.preventDefault(), conduct.mutate())}>
        <h2>
          Conduct audit <code>{a.id}</code>
        </h2>
        <p className="muted">
          {a.scope} (period {a.periodFrom} to {a.periodTo}), planned for {a.plannedFor}. Once recorded the findings are final; each follow-up stays open until CER completes it in the follow-up register.
          Each finding names an obligation version in force during the audit period.
        </p>
        <ProblemAlert error={obligations.error} />
        {obligations.data && applicable.length === 0 && <p className="error">No obligation version was in force during the audit period; publish one under Compliance → Obligations before recording findings.</p>}
        <Field label="Conducted on">
          <input type="date" value={conductedOn} min={a.plannedFor} max={todayLocal()} onChange={(e) => setConductedOn(e.target.value)} required disabled={busy} />
        </Field>
        <table className="table">
          <thead>
            <tr>
              <th>Finding</th>
              <th>Obligation</th>
              <th>Follow-up action</th>
              <th>Due</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, n) => (
              <tr key={n}>
                <td>
                  <textarea aria-label="Finding" rows={2} value={r.finding} onChange={(e) => set(n, { finding: e.target.value })} disabled={busy} />
                </td>
                <td>
                  <select aria-label="Obligation" value={r.obligationId} onChange={(e) => set(n, { obligationId: e.target.value })} disabled={busy}>
                    <option value="">Select…</option>
                    {applicable.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.code} v{o.version} ({o.validFrom} → {o.validTo ?? 'open'}): {o.title}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <textarea aria-label="Follow-up action" rows={2} value={r.followUpAction} onChange={(e) => set(n, { followUpAction: e.target.value })} disabled={busy} />
                </td>
                <td>
                  <input type="date" aria-label="Due" value={r.dueOn} min={conductedOn} onChange={(e) => set(n, { dueOn: e.target.value })} disabled={busy} />
                </td>
                <td>
                  {rows.length > 1 && (
                    <button type="button" onClick={() => setRows(rows.filter((_, k) => k !== n))} disabled={busy}>
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div>
          <button type="button" onClick={() => setRows([...rows, blank()])} disabled={busy}>
            Add finding
          </button>
        </div>
        <Field label="Evidence reference" hint="Working papers, report ID or URL of the evidence held by CER.">
          <input value={evidenceRef} onChange={(e) => setEvidenceRef(e.target.value)} disabled={busy} />
        </Field>
        <Field label="Summary">
          <textarea rows={2} value={summary} onChange={(e) => setSummary(e.target.value)} required disabled={busy} />
        </Field>
        <ProblemAlert error={conduct.error} />
        <div>
          <button type="submit" disabled={busy || !complete || summary.trim().length < 3}>
            Record audit
          </button>
        </div>
      </form>
      <form className="form card" onSubmit={(e) => (e.preventDefault(), cancel.mutate())}>
        <h2>
          Cancel audit <code>{a.id}</code>
        </h2>
        <Field label="Reason">
          <input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} required disabled={busy} />
        </Field>
        <ProblemAlert error={cancel.error} />
        <div>
          <button type="submit" disabled={busy || cancelReason.trim().length < 3}>
            Cancel audit
          </button>
        </div>
      </form>
    </>
  );
}

function CloseAudit({ a }: { a: Audit }) {
  const refresh = useRefresh();
  const [summary, setSummary] = useState('');
  const close = useMutation({ mutationFn: () => auditsApi.close(a.id, { summary }), onSuccess: refresh });
  const open = a.findings.filter((f) => f.status === 'open').length;
  return (
    <form className="form card" onSubmit={(e) => (e.preventDefault(), close.mutate())}>
      <h2>
        Close audit <code>{a.id}</code>
      </h2>
      <p className="muted">{open ? `${open} follow-up${open === 1 ? '' : 's'} still open; they stay in the follow-up register after the audit closes.` : 'All follow-ups are completed.'}</p>
      <Field label="Summary">
        <textarea rows={2} value={summary} onChange={(e) => setSummary(e.target.value)} required disabled={close.isPending} />
      </Field>
      <ProblemAlert error={close.error} />
      <div>
        <button type="submit" disabled={close.isPending || summary.trim().length < 3}>
          Close audit
        </button>
      </div>
    </form>
  );
}

function CompleteFollowUp({ f, onDone }: { f: FollowUp; onDone: () => void }) {
  const refresh = useRefresh();
  const [note, setNote] = useState('');
  const [evidenceRef, setEvidenceRef] = useState('');
  const complete = useMutation({ mutationFn: () => followUpsApi.complete(f.id, { note, evidenceRef: evidenceRef.trim() || undefined }), onSuccess: () => (refresh(), onDone()) });
  return (
    <form className="form" onSubmit={(e) => (e.preventDefault(), complete.mutate())}>
      <Field label="Completion note">
        <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} required disabled={complete.isPending} />
      </Field>
      <Field label="Evidence reference">
        <input value={evidenceRef} onChange={(e) => setEvidenceRef(e.target.value)} disabled={complete.isPending} />
      </Field>
      <ProblemAlert error={complete.error} />
      <div className="row">
        <button type="submit" disabled={complete.isPending || note.trim().length < 3}>
          Mark completed
        </button>
        <button type="button" onClick={onDone} disabled={complete.isPending}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function FollowUpTable({ items, mode, casePath }: { items: FollowUp[]; mode: 'cer' | 'participant'; casePath: string }) {
  const [completing, setCompleting] = useState<number | null>(null);
  if (items.length === 0) return <p>No follow-ups.</p>;
  return (
    <table className="table">
      <thead>
        <tr>
          {mode === 'cer' && <th>Participant</th>}
          <th>Audit</th>
          <th>Finding</th>
          <th>Obligation</th>
          <th>Follow-up action</th>
          <th>Due</th>
          <th>Status</th>
          {mode === 'cer' && <th />}
        </tr>
      </thead>
      <tbody>
        {items.map((f) => (
          <tr key={f.id}>
            {mode === 'cer' && <td>{f.subjectParticipantName}</td>}
            <td>
              <code>{f.auditId}</code> #{f.seq} <StatusBadge status={f.auditStatus} />
              <div>
                <Link to={`${casePath}/${f.caseId}`}>{f.caseTitle}</Link>
              </div>
            </td>
            <td style={{ whiteSpace: 'pre-wrap' }}>{f.finding}</td>
            <td>
              <code>{f.obligationRef}</code>
            </td>
            <td style={{ whiteSpace: 'pre-wrap' }}>{f.followUpAction}</td>
            <td>{f.dueOn}</td>
            <td>
              <FollowUpState f={f} />
            </td>
            {mode === 'cer' && (
              <td>
                {f.status === 'open' &&
                  (completing === f.id ? (
                    <CompleteFollowUp f={f} onDone={() => setCompleting(null)} />
                  ) : (
                    <button type="button" onClick={() => setCompleting(f.id)}>
                      Complete…
                    </button>
                  ))}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** The CER follow-up register (C5): every audit follow-up, filtered by participant, status and overdue; CER records completion. */
export function FollowUpsPage() {
  const [params, setParams] = useSearchParams();
  const participantId = params.get('participantId') ?? '';
  const status = (params.get('status') ?? '') as FollowUpStatus | '';
  const overdue = params.get('overdue') === 'true';
  const auditId = params.get('auditId') ?? '';
  const query: FollowUpQuery = { participantId: participantId || undefined, status: status || undefined, overdue: overdue ? 'true' : undefined, auditId: auditId || undefined };
  const list = useQuery({ queryKey: ['follow-ups', query], queryFn: () => followUpsApi.list(query) });
  const participants = useQuery({ queryKey: ['participants', '', 'approved'], queryFn: () => participantsApi.search('', 'approved') });
  const set = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p);
  };
  return (
    <>
      <div className="row">
        <h1>Follow-up register</h1>
        <span className="grow" />
        <Link to="/cer/compliance/cases">Cases →</Link>
      </div>
      <p className="muted">Follow-up actions from audit findings, each against an obligation with a due date. They stay open after the audit closes until CER records completion; overdue means open past its due date (Sydney time).</p>
      <div className="row">
        <select value={participantId} onChange={(e) => set('participantId', e.target.value)} aria-label="Participant filter">
          <option value="">All participants</option>
          {participants.data?.items.map((p) => (
            <option key={p.id} value={p.id}>
              {p.legalName}
            </option>
          ))}
        </select>
        <select value={status} onChange={(e) => set('status', e.target.value)} aria-label="Status filter">
          <option value="">All statuses</option>
          <option value="open">open</option>
          <option value="completed">completed</option>
        </select>
        <label>
          <input type="checkbox" checked={overdue} onChange={(e) => set('overdue', e.target.checked ? 'true' : '')} /> Overdue only
        </label>
        {auditId && (
          <span>
            Audit <code>{auditId}</code>{' '}
            <button type="button" onClick={() => set('auditId', '')}>
              Clear
            </button>
          </span>
        )}
      </div>
      <ProblemAlert error={list.error ?? participants.error} />
      {list.data && <FollowUpTable items={list.data.items} mode="cer" casePath="/cer/compliance/cases" />}
    </>
  );
}

/** The participant's audit follow-ups across its cases, on its Compliance page (read-only; RLS limits them to its own). */
export function ParticipantFollowUps({ basePath }: { basePath: string }) {
  const list = useQuery({ queryKey: ['follow-ups', 'mine'], queryFn: () => followUpsApi.list(), staleTime: 0, gcTime: 0 });
  return (
    <>
      <h2>Follow-ups</h2>
      <p className="muted">Follow-up actions from CER audits of your organisation, each against an obligation with a due date. CER records completion; attach evidence of remediation on the case.</p>
      <ProblemAlert error={list.error} />
      {list.data && <FollowUpTable items={list.data.items} mode="participant" casePath={basePath} />}
    </>
  );
}
